import { useState, useEffect } from 'react';
import {
  TextField,
  Grid,
  InputAdornment,
  FormControlLabel,
  Switch,
  Box,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { ICarrier, ICustomer } from '@/types';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useUSDOTForCarrier, useGetUsDotDataForCustomer } from '@/hooks/useGetUsDotData';
import { Search as FaSearch } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import apiService from '@/service/apiService';
import { EntityType } from '@/components/common/UniversalEntityForm';
import { useAppSelector } from '@/redux/store';
import UsDotField from './commonFields/UsDotField';
import McNUmberFiled from './commonFields/McNUmberFiled';

interface CompanySectionProps {
  entity: EntityType
  localUsdot?: string;
  setLocalUsdot?: (usdot: string) => void;
  showUSDOT?: boolean;
  showMCNumber?: boolean;
  showRate?: boolean;
  showNickName?: boolean;
  showDisplayName?: boolean;
  showMobileNo?: boolean;
  showFax?: boolean;
  showOther?: boolean;
  showWebsite?: boolean;
  showNameToPrintOnCheck?: boolean;
  showIsSubCustomer?: boolean;
  showIsSubVendor?: boolean;
  showAddress?: boolean;
  id?: string;
}

const CompanySection: React.FC<CompanySectionProps> = ({
  entity,
  localUsdot: externalLocalUsdot,
  setLocalUsdot: externalSetLocalUsdot,
  showUSDOT = true,
  showMCNumber = true,
  showRate = true,
  showNickName = false,
  showDisplayName = false,
  showMobileNo = false,
  showFax = false,
  showOther = false,
  showWebsite = false,
  showNameToPrintOnCheck = false,
  showIsSubCustomer = false,
  showIsSubVendor = false,
  showAddress = true,
  id
}) => {
  const isRepairCompany= useAppSelector((state)=>state.user.currentCompanyDetails.type==="REPAIR SHOP")
  const form = useFormContext<ICarrier | ICustomer>();
  const usdot = form.watch('usdot');
  const withoutUsdot = form.watch('withoutUsdot');
  const isSubCustomer = form.watch('isSubCustomer');
  const isSubVendor = form.watch('isSubVendor');

  // Fetch parent customers for sub-customer selection
  const { data: parentCustomersData } = useQuery({
    queryKey: ['customers', { limit: 1000, page: 1, search: "" }],
    queryFn: () => apiService.getCustomers({ limit: 1000, page: 1, search: "" }),
    enabled: showIsSubCustomer && !!isSubCustomer,
  });

  // Fetch parent vendors for sub-vendor selection
  const { data: parentVendorsData } = useQuery({
    queryKey: ['vendors', { limit: 1000, page: 1, search: "", isAll: "" }],
    queryFn: () => apiService.getVendors({ limit: 1000, page: 1, search: "", isAll: "" }),
    enabled: showIsSubVendor && !!isSubVendor,
  });

  // Handle local USDOT state differently for carriers vs customers
  const [internalLocalUsdot, setInternalLocalUsdot] = useState<string>(usdot || '');
  const localUsdot = entity === 'carrier' ? externalLocalUsdot || '' : internalLocalUsdot;
  const setLocalUsdot = entity === 'carrier' ? externalSetLocalUsdot : setInternalLocalUsdot;

  // Use appropriate hook based on entity type
  const carrierHook = entity === 'carrier' ? useUSDOTForCarrier(form.setValue) : { loading: false, error: null, handleSubmit: () => { } };
  const customerHook = entity === 'load-customer' ? useGetUsDotDataForCustomer(form.setValue) : { loading: false, error: null, handleSubmit: () => { } };

  const { loading: usdotLoading, error: usdotError, handleSubmit: searchUSDOT } = entity === 'carrier' ? carrierHook : customerHook;

  useEffect(() => {
    if (entity !== 'carrier') {
      setInternalLocalUsdot(usdot || '');
    }
  }, [usdot, entity]);

  const handleUSDOTSearch = () => {
    const usdotValue = form.watch('usdot');
    if (usdotValue) {
      searchUSDOT(usdotValue);
      setLocalUsdot?.(usdotValue);
    }
  };

  const handleToggleWithoutUsdot = () => {
    const next = !withoutUsdot;
    form.setValue('withoutUsdot', next);
    if (next) {
      form.setValue('usdot' as any, '');
      if (entity === 'carrier') {
        setLocalUsdot?.('');
      } else {
        setInternalLocalUsdot('');
      }
      form.setValue('entityDetails' as any, {
        entity_type: '',
        dba_name: '',
        legal_name: '',
        operating_status: '',
        physical_address: '',
        mailing_address: '',
        carrier_operation: [],
        out_of_service_date: '',
      });
    }
  };

  return (
    <Grid container spacing={1.75}>
      {/* USDOT field - shown first when available and Without USDOT is false */}
      {isRepairCompany ? <UsDotField setLocalUsdot={setLocalUsdot} entity={entity}/>: showUSDOT && !withoutUsdot  && ['carrier', 'load-customer'].includes(entity) && (
       <UsDotField setLocalUsdot={setLocalUsdot} entity={entity}/>
      )}

      {/* Company Name, Email, and Phone with dynamic column sizing based on USDOT visibility */}
      {(() => {
        const showUsdotField = showUSDOT && !withoutUsdot;
        const getColumnSize = () => showUsdotField ? 4 : 4; // Always 4 since we have 3 fields

        return (
          <>
           {/* M C Number */}
            { isRepairCompany && (
              <McNUmberFiled columnSize={getColumnSize()}/>
            )}
            {/* Company Name - always shown */}
            <Grid item xs={12} md={getColumnSize()}>
              <Controller
                name="company"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Company Name"
                    size="small"
                    error={!!form.formState.errors.company}
                    helperText={form.formState.errors.company?.message}
                  />
                )}
              />
            </Grid>

            {/* Nick Name - shown when enabled */}
            {showNickName && (
              <Grid item xs={12} md={getColumnSize()}>
                <Controller
                  name="nickName"
                  control={form.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Nick Name"
                      size="small"
                      error={!!(form.formState.errors as any).nickName}
                      helperText={(form.formState.errors as any).nickName?.message}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Email - always shown */}
            <Grid item xs={12} md={getColumnSize()}>
              <Controller
                name="email"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email"
                    size="small"
                    error={!!form.formState.errors.email}
                    helperText={form.formState.errors.email?.message}
                  />
                )}
              />
            </Grid>

            {/* Phone - always shown */}
            <Grid item xs={12} md={getColumnSize()}>
              <Controller
                name="phone"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Phone"
                    size="small"
                    onChange={(e) => {
                      field.onChange(e);
                    }}
                    error={!!form.formState.errors.phone}
                    helperText={form.formState.errors.phone?.message}
                  />
                )}
              />
            </Grid>
          </>
        );
      })()}

      {/* Without USDOT toggle - placed in separate row for better spacing, hidden for carriers */}

      {/* Modern Without USDOT Section */}
      {showUSDOT && ['load-customer'].includes(entity) && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              borderRadius: '6px',
              background: withoutUsdot
                ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
              border: withoutUsdot
                ? '1px solid #10b98130'
                : '1px solid #6366f130',
              transition: 'all 0.3s ease',
              //boxShadow: withoutUsdot
                //</Grid>? '0 4px 15px rgba(16,185,129,0.12)'
               // : '0 4px 15px rgba(99,102,241,0.08)',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: {xs:'13px', md:'14px'},
                  fontWeight: 700,
                  color: '#0f172a',
                  mb: 0.3,
                }}
              >
                Without USDOT
              </Typography>

              <Typography
                sx={{
                  fontSize: {xs:'12px', md:'13px'},
                  color: '#64748b',
                  fontWeight: 500,
                }}
              >
                Enable this if customer has no USDOT number
              </Typography>
            </Box>

            <Switch
              checked={withoutUsdot}
              onChange={handleToggleWithoutUsdot}
              disableRipple
              sx={{
                width: 42,
                height: 20,
                padding: 0,
                '& .MuiSwitch-switchBase': {
                  padding: '2px',
                  transitionDuration: '300ms',
                  '&.Mui-checked': {
                    transform: 'translateX(22px)',
                    color: '#fff',
                    '& + .MuiSwitch-track': {
                      backgroundColor: '#10b981',
                      opacity: 1,
                      border: 0,
                    },
                  },
                },

                '& .MuiSwitch-thumb': {
                  boxSizing: 'border-box',
                  width: 16,
                  height: 16,
                  //boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                },

                '& .MuiSwitch-track': {
                  borderRadius: 20,
                  backgroundColor: '#cbd5e1',
                  opacity: 1,
                  transition: 'all 0.3s ease',
                },
              }}
            />
          </Box>
        </Grid>
      )}

      {/* Display Name - shown when enabled */}
      {showDisplayName && (
        <Grid item xs={12} md={isRepairCompany?4: 4}>
          <Controller
            name="displayCustomerName"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={entity === 'vendor' ? "Display Vendor Name" : "Display Customer Name"}
                size="small"
                error={!!form.formState.errors.displayCustomerName}
                helperText={form.formState.errors.displayCustomerName?.message}
              />
            )}
          />
        </Grid>
      )}
      {isRepairCompany && (
        <Grid item xs={12} md={4}>
          <Controller
            name="address"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Address"
                size="small"
                error={!!form.formState.errors.address}
                helperText={form.formState.errors.address?.message}
                 InputLabelProps={{
                            shrink: Boolean(field.value?.toString().trim()),
                        }}
              />
            )}
          />
        </Grid>
            )}

      {/* Mobile No - shown when enabled */}
      {showMobileNo && (
        <Grid item xs={12} md={4}>
          <Controller
            name="mobileNo"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Mobile No"
                size="small"
                onChange={(e) => {
                  field.onChange(e);
                }}
                error={!!form.formState.errors.mobileNo}
                helperText={form.formState.errors.mobileNo?.message}
              />
            )}
          />
        </Grid>
      )}

      {/* Fax - shown when enabled */}
      {showFax && (
        <Grid item xs={12} md={4}>
          <Controller
            name="fax"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Fax"
                size="small"
                error={!!form.formState.errors.fax}
                helperText={form.formState.errors.fax?.message}
              />
            )}
          />
        </Grid>
      )}

      {/* Other - shown when enabled */}
      {showOther && (
        <Grid item xs={12} md={4}>
          <Controller
            name="other"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Other"
                size="small"
                error={!!form.formState.errors.other}
                helperText={form.formState.errors.other?.message}
              />
            )}
          />
        </Grid>
      )}

      {/* Website - shown when enabled */}
      {showWebsite && (
        <Grid item xs={12} md={4}>
          <Controller
            name="website"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Website"
                size="small"
                error={!!form.formState.errors.website}
                helperText={form.formState.errors.website?.message}
              />
            )}
          />
        </Grid>
      )}

      {/* Name To Print On Check - shown when enabled */}
      {showNameToPrintOnCheck && !isRepairCompany && (
        <Grid item xs={12} md={4}>
          <Controller
            name="nameToPrintOnCheck"
            control={form.control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Name To Print On Check"
                size="small"
                error={!!(form.formState.errors as any).nameToPrintOnCheck}
                helperText={(form.formState.errors as any).nameToPrintOnCheck?.message}
              />
            )}
          />
        </Grid>
      )}

      {/* Is Sub Customer - shown when enabled */}
      {showIsSubCustomer && !isRepairCompany && (
        <>
          <Grid item xs={12} md={12} >
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '36px', justifyContent:'flex-start' }}>
              <Controller
                name="isSubCustomer"
                control={form.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox size='small'
                        {...field}
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                        sx={{
                          padding:'0px 5px 0px 15px',
                          '& .MuiSvgIcon-root': {
                            fontSize: 18
                          }
                        }}
                      />
                    }
                    label="Is Sub Customer"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: "14px",
                        fontWeight: 500,
                        color: '#101721'
                      }
                    }}
                  />
                )}
              />
            </Box>
          </Grid>

          {/* Parent Customer dropdown - shown when Is Sub Customer is checked */}
          {isSubCustomer && (
            <Grid item xs={12} md={12}>
              <Controller
                name="parentCustomer"
                control={form.control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Parent Customer</InputLabel>
                    <Select
                      {...field}
                      label="Parent Customer"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      {parentCustomersData?.data?.filter((customer: any) => customer._id !== id).map((customer: any) => (
                        <MenuItem key={customer._id} value={customer._id}>
                          {customer.company}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          )}
        </>
      )}

      {/* Is Sub Vendor - shown when enabled */}
      {showIsSubVendor &&  (
        <>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '36px' }}>
              <Controller
                name="isSubVendor"
                control={form.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox size='small'
                        {...field}
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        sx={{
                          padding:'0px 5px 0px 15px',
                          '& .MuiSvgIcon-root': {
                              fontSize: 18
                            }
                        }}
                      />
                    }
                    label="Is Sub Vendor"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: {xs:'14px', md:'14px'},
                        fontWeight: 500,
                        color: '#101721'
                      }
                    }}
                  />
                )}
              />
            </Box>
          </Grid>

          {/* Parent Vendor dropdown - shown when Is Sub Vendor is checked */}
          {isSubVendor && (
            <Grid item xs={12} md={12}>
              <Controller
                name="parentVendor"
                control={form.control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel>Parent Vendor</InputLabel>
                    <Select
                      {...field}
                      label="Parent Vendor"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      {parentVendorsData?.data?.filter((vendor: any) => vendor._id !== id).map((vendor: any) => (
                        <MenuItem key={vendor._id} value={vendor._id}>
                          {vendor.company}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          )}
        </>
      )}

      {/* MC Number and Rate fields with dynamic column sizing */}
      {(() => {
        const showMcNumber = showMCNumber && !withoutUsdot;
        const showRateField = entity === 'carrier' && showRate;
        const visibleFieldsCount = [showMcNumber, showRateField].filter(Boolean).length;

        if (visibleFieldsCount === 0) return null;

        const getColumnSize = () => {
          switch (visibleFieldsCount) {
            case 1: return 12;
            case 2: return 6;
            default: return 4;
          }
        };

        const columnSize = getColumnSize();

        return (
          <>
            {/* MC Number - shown when enabled and Without USDOT is false */}
            {showMcNumber && !isRepairCompany && (
              <McNUmberFiled columnSize={columnSize}/>
            )}

            {/* Rate - shown only for carrier entities */}
            {showRateField && (
              <Grid item xs={12} md={columnSize}>
                <Controller
                  name="rate"
                  control={form.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Rate"
                      size="small"
                      type="number"
                      error={!!(form.formState.errors as any).rate}
                      helperText={(form.formState.errors as any).rate?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </>
        );
      })()}

      {/* VAT Number and UTR Number - shown for customers only */}
      {entity === 'load-customer' && (
        <>
          <Grid item xs={12} md={6}>
            <Controller
              name="vatNumber"
              control={form.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="VAT Number"
                  size="small"
                  error={!!(form.formState.errors as any).vatNumber}
                  helperText={(form.formState.errors as any).vatNumber?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="utrNumber"
              control={form.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="UTR Number"
                  size="small"
                  error={!!(form.formState.errors as any).utrNumber}
                  helperText={(form.formState.errors as any).utrNumber?.message}
                />
              )}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
export default CompanySection;